import { useMutation } from '@tanstack/react-query';
import { agentExecutionClient } from '@terra/shared-rest-clients-agent-execution';
import { useInvalidateAgentExecutionHistory } from './useAgentExecutionHistory';

type UseEvidenceMutationsProps = {
  executionId: string;
};

export function useEvidenceMutations({
  executionId,
}: UseEvidenceMutationsProps) {
  const invalidateHistory = useInvalidateAgentExecutionHistory();

  const addEvidence = useMutation({
    mutationFn: async ({
      artifactId,
      description,
    }: {
      artifactId: string;
      description: string;
    }) => {
      const response = await agentExecutionClient.addEvidence({
        params: { executionId },
        body: { artifactId, description },
      });

      if (response.status !== 200) {
        throw new Error('Failed to add evidence');
      }

      return response.body;
    },
    onSuccess: () => {
      invalidateHistory(executionId);
    },
  });

  const updateEvidence = useMutation({
    mutationFn: async ({
      artifactId,
      description,
    }: {
      artifactId: string;
      description: string;
    }) => {
      const response = await agentExecutionClient.updateEvidence({
        params: { executionId },
        body: { artifactId, description },
      });

      if (response.status !== 200) {
        throw new Error('Failed to update evidence');
      }

      return response.body;
    },
    onSuccess: () => {
      invalidateHistory(executionId);
    },
  });

  const removeEvidence = useMutation({
    mutationFn: async ({ artifactId }: { artifactId: string }) => {
      const response = await agentExecutionClient.removeEvidence({
        params: { executionId, artifactId },
        body: {},
      });

      if (response.status !== 200) {
        throw new Error('Failed to remove evidence');
      }

      return response.body;
    },
    onSuccess: () => {
      invalidateHistory(executionId);
    },
  });

  return { addEvidence, updateEvidence, removeEvidence };
}
